import { DEFAULT_TABLE_HEADER_COLOR } from '@/constants';
import { TableBlock, TableCell, TableCellColor, TableRow, TextBlock } from '@/types/report-payload';

type TableCellInput = {
    id?: string;
    value?: string | number | null;
    rowSpan?: number;
    colSpan?: number;
    mergedInto?: string;
    color?: TableCellColor;
};

type TableRowInput = {
    id?: string;
    cells?: TableCellInput[];
    source_test_id?: string;
    source_provider?: 'satepsi';
};

/** As faixas entram no reparo pelo mesmo caminho das outras linhas. */
type LegacyHeaderBands = TableRowInput[];

type LegacyHeader = {
    column_name?: string;
};

type LegacyTableBlock = Omit<TableBlock, 'header' | 'header_bands' | 'rows' | 'columnWidths'> & {
    header?: TableRowInput;
    header_bands?: LegacyHeaderBands;
    rows?: TableRowInput[] | Array<Array<string | number | null>>;
    columnWidths?: number[];
    headers?: LegacyHeader[];
};

export const createId = (prefix: string) =>
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeCellValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '';
    return typeof value === 'string' ? value : String(value);
};

export const createTableCell = (value: string | number | null = ''): TableCell => {
    return {
        id: createId('cell'),
        value: normalizeCellValue(value),
    };
};

export const createTableRow = (
    columnCount: number,
    values?: Array<string | number | null>,
): TableRow => {
    return {
        id: createId('row'),
        cells: Array.from({ length: columnCount }, (_, index) => createTableCell(values?.[index])),
    };
};

export const normalizeTableRow = (input: TableRowInput, columnCount: number): TableRow => {
    const cellsInput = input.cells || [];
    const cells = Array.from({ length: columnCount }, (_, index) => {
        const cell = cellsInput[index];
        if (!cell) return createTableCell('');

        return {
            id: cell.id || createId('cell'),
            value: normalizeCellValue(cell.value),
            rowSpan: cell.rowSpan,
            colSpan: cell.colSpan,
            mergedInto: cell.mergedInto,
            color: cell.color,
        };
    });

    return {
        id: input.id || createId('row'),
        cells,
        source_test_id: input.source_test_id,
        source_provider: input.source_provider,
    };
};

/**
 * `table.header_color` guarda ou um hex, ou o literal `'primary'` — o mesmo
 * sentinela que `divider.background_color` usa. Guardar o sentinela (e não uma
 * cópia do hex) é o que faz o cabeçalho acompanhar a cor primária quando ela
 * muda depois. Todo caminho de render (editor, leitura, modais e o .docx da
 * api) precisa resolver por aqui, senão o mesmo laudo sai com cabeçalhos
 * diferentes em cada saída.
 */
export const resolveTableHeaderColor = (
    headerColor: string | undefined,
    primaryColor: string | undefined,
): string => {
    if (headerColor === 'primary') return primaryColor || DEFAULT_TABLE_HEADER_COLOR;

    return headerColor || DEFAULT_TABLE_HEADER_COLOR;
};

export const getContrastTextColor = (hexColor: string): string => {
    if (!hexColor || !hexColor.startsWith('#')) return '#0f172a';

    const r = parseInt(hexColor.substring(1, 3), 16);
    const g = parseInt(hexColor.substring(3, 5), 16);
    const b = parseInt(hexColor.substring(5, 7), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;

    return yiq >= 128 ? '#0f172a' : '#ffffff';
};

/**
 * As linhas do cabeçalho, de cima para baixo, com a FOLHA sempre por último.
 *
 * Este é o único lugar do front que sabe como `header_bands` e `header` se
 * empilham. Todo o resto — gráficos, coluna Classificação, `columnWidths` —
 * continua lendo `block.header` direto e por isso não precisa mudar.
 */
export const getHeaderRows = (block: TableBlock): TableRow[] =>
    block.header_bands?.length ? [...block.header_bands, block.header] : [block.header];

export const getHeaderRowCount = (block: TableBlock): number =>
    (block.header_bands?.length ?? 0) + 1;

/**
 * Devolve o bloco com a pilha de cabeçalho trocada, preservando o invariante:
 * a última linha vira `header`, as demais viram as faixas.
 *
 * Com uma linha só a chave `header_bands` é **omitida**, não gravada como `[]`
 * — o schema da API é `.strict()`, e um laudo antigo tem de sair daqui idêntico
 * ao que entrou.
 */
export const withHeaderRows = (block: TableBlock, rows: TableRow[]): TableBlock => {
    if (rows.length === 0) return block;

    const header = rows[rows.length - 1];
    const bands = rows.slice(0, -1);
    const next = { ...block, header };

    if (bands.length > 0) {
        next.header_bands = bands;
    } else {
        delete next.header_bands;
    }

    return next;
};

export const buildCellPositionMap = (block: TableBlock) => {
    const map = new Map<
        string,
        { section: 'header' | 'body'; rowIndex: number; colIndex: number }
    >();
    getHeaderRows(block).forEach((row, rowIndex) => {
        row.cells.forEach((cell, colIndex) => {
            map.set(cell.id, { section: 'header', rowIndex, colIndex });
        });
    });

    block.rows.forEach((row, rowIndex) => {
        row.cells.forEach((cell, colIndex) => {
            map.set(cell.id, { section: 'body', rowIndex, colIndex });
        });
    });

    return map;
};

export const getCellAt = (
    block: TableBlock,
    section: 'header' | 'body',
    rowIndex: number,
    colIndex: number,
) => {
    if (section === 'header') {
        return getHeaderRows(block)[rowIndex]?.cells[colIndex] || null;
    }

    const row = block.rows[rowIndex];
    if (!row) return null;
    return row.cells[colIndex] || null;
};

export const normalizeColumnWidths = (block: TableBlock) => {
    const columnCount = block.header?.cells?.length || block.columnWidths.length || 1;
    if (block.columnWidths.length === columnCount) return block.columnWidths;
    return Array.from({ length: columnCount }, (_, index) => {
        return block.columnWidths[index] || 150;
    });
};

export const getHeaderLabels = (header: TableRow) => {
    const idToIndex = new Map<string, number>();
    header.cells.forEach((cell, index) => {
        idToIndex.set(cell.id, index);
    });

    return header.cells.map((cell) => {
        if (cell.mergedInto) {
            const originIndex = idToIndex.get(cell.mergedInto);
            if (originIndex !== undefined) {
                return header.cells[originIndex].value;
            }
        }
        return cell.value;
    });
};

const resolveColumnCount = (block: LegacyTableBlock) => {
    const headerCount = block.header?.cells?.length || block.headers?.length || 0;
    const rowCounts = Array.isArray(block.rows)
        ? block.rows.map((row) => {
              if (Array.isArray(row)) return row.length;
              return row?.cells?.length || 0;
          })
        : [];
    const rowCount = rowCounts.length ? Math.max(...rowCounts) : 0;
    const widthCount = block.columnWidths?.length || 0;
    const bandCounts = (block.header_bands ?? []).map((band) => band?.cells?.length || 0);
    const bandCount = bandCounts.length ? Math.max(...bandCounts) : 0;

    return Math.max(headerCount, rowCount, widthCount, bandCount, 1);
};

const normalizeLegacyHeader = (block: LegacyTableBlock, columnCount: number) => {
    if (block.header?.cells) {
        return normalizeTableRow(block.header, columnCount);
    }

    const legacyHeaders = block.headers || [];
    const values = Array.from({ length: columnCount }, (_, index) => {
        return legacyHeaders[index]?.column_name || '';
    });

    return createTableRow(columnCount, values);
};

/**
 * Repara as faixas com a mesma régua das outras linhas. Devolve `undefined`
 * quando não há faixa, para que a chave continue ausente do bloco.
 */
const normalizeLegacyHeaderBands = (block: LegacyTableBlock, columnCount: number) => {
    const bands = block.header_bands;
    if (!bands?.length) return undefined;
    return bands.map((band) => normalizeTableRow(band || {}, columnCount));
};

const normalizeLegacyRows = (block: LegacyTableBlock, columnCount: number) => {
    if (!block.rows) return [];
    if (Array.isArray(block.rows) && block.rows.length === 0) return [];

    if (Array.isArray(block.rows) && Array.isArray(block.rows[0])) {
        return (block.rows as Array<Array<string | number | null>>).map((row) =>
            createTableRow(columnCount, row),
        );
    }

    return (block.rows as TableRowInput[]).map((row) => normalizeTableRow(row || {}, columnCount));
};

const normalizeLegacyColumnWidths = (block: LegacyTableBlock, columnCount: number) => {
    const widths = block.columnWidths || [];
    return Array.from({ length: columnCount }, (_, index) => widths[index] || 150);
};

const isNormalized = (block: TableBlock, columnCount: number) => {
    if (!block.header?.cells || block.header.cells.length !== columnCount) return false;
    if (block.columnWidths?.length !== columnCount) return false;
    if (!Array.isArray(block.rows)) return false;

    // Uma faixa curta desalinha o `colgroup` e passa batido pelo zod da API, que
    // só confere a folha. Se não checarmos aqui, o early-return devolve o bloco
    // torto sem reparar.
    if (block.header_bands) {
        if (!Array.isArray(block.header_bands)) return false;
        const bandsValid = block.header_bands.every(
            (band) => Array.isArray(band?.cells) && band.cells.length === columnCount,
        );
        if (!bandsValid) return false;
    }

    const rowsValid = block.rows.every(
        (row) => Array.isArray(row.cells) && row.cells.length === columnCount,
    );

    return rowsValid;
};

export const normalizeTableBlock = (block: LegacyTableBlock): TableBlock => {
    const columnCount = resolveColumnCount(block);
    const normalizedHeader = normalizeLegacyHeader(block, columnCount);
    const normalizedBands = normalizeLegacyHeaderBands(block, columnCount);
    const normalizedRows = normalizeLegacyRows(block, columnCount);
    const normalizedWidths = normalizeLegacyColumnWidths(block, columnCount);

    const next: TableBlock = {
        ...block,
        header: normalizedHeader,
        rows: normalizedRows,
        columnWidths: normalizedWidths,
    } as TableBlock;

    if (normalizedBands) {
        next.header_bands = normalizedBands;
    } else {
        delete next.header_bands;
    }

    if (isNormalized(block as TableBlock, columnCount)) {
        return block as TableBlock;
    }

    return next;
};

/**
 * Creates an empty text block with a unique ID.
 * When `linkedTableId` is provided the block is treated as the
 * AI-interpretation companion of that table (shows the "Interpretar com IA" action).
 */
export const createTextBlock = (linkedTableId?: string): TextBlock => ({
    id: createId('block'),
    block_type: 'text',
    order: 0,
    has_content: true,
    automatic: false,
    content: '',
    ...(linkedTableId ? { linked_table_id: linkedTableId } : {}),
});

/**
 * Creates a deep clone of a TableBlock, shallow-cloning each cell.
 * Pass `columnWidths` to override the block's widths in the clone.
 */
export const cloneTableBlock = (block: TableBlock, columnWidths?: number[]): TableBlock => ({
    ...block,
    // Sem clonar as faixas, o spread acima as levaria por REFERÊNCIA — e
    // `TableActions` muta células no lugar, então uma edição atravessaria a
    // fronteira da cópia (clipboard de bloco, colar, desfazer).
    ...(block.header_bands
        ? {
              header_bands: block.header_bands.map((band) => ({
                  ...band,
                  cells: band.cells.map((cell) => ({ ...cell })),
              })),
          }
        : {}),
    header: {
        ...block.header,
        cells: block.header.cells.map((cell) => ({ ...cell })),
    },
    rows: block.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell) => ({ ...cell })),
    })),
    columnWidths: [...(columnWidths ?? block.columnWidths)],
});
